/**
 * 倒计时以北京时间（Asia/Shanghai, UTC+8）为准，不再请求服务器时间接口。
 * end-time 支持 Unix 秒，或北京时间墙钟：2026-12-31 23:59:59
 */
const BEIJING_OFFSET = '+08:00';

/**
 * @param {string|number|null|undefined} value
 * @returns {number}
 */
function parseBeijingEndTime(value) {
  if (value == null || value === '') return NaN;
  const raw = String(value).trim();
  if (/^\d+$/.test(raw)) return parseInt(raw, 10);

  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    const iso = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6] || '00'}${BEIJING_OFFSET}`;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? NaN : Math.floor(ms / 1000);
  }

  const fallback = new Date(raw).getTime();
  return Number.isNaN(fallback) ? NaN : Math.floor(fallback / 1000);
}

function nowSeconds() {
  return Math.floor(Date.now() / 1000);
}

class CountdownEasy {
  /**
   * @param {object} options
   * @param {number} options.endTime
   * @param {number} [options.beginTime]
   * @param {string} [options.beginLabel]
   * @param {string} [options.endLabel]
   * @param {function} [options.onUpdate]
   * @param {function} [options.onFinish]
   */
  constructor(options) {
    if (!options || !options.endTime) {
      throw new Error('CountdownEasy: endTime is required.');
    }

    this.endTime = options.endTime;
    this.beginTime = options.beginTime;
    this.beginLabel = options.beginLabel || '';
    this.endLabel = options.endLabel || '';
    this.onUpdate = options.onUpdate;
    this.onFinish = options.onFinish;
    this.timer = null;
  }

  /**
   * @param {number} totalSeconds
   */
  _formatTime(totalSeconds) {
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return {
      days: String(days).padStart(2, '0'),
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    };
  }

  start() {
    const currentTime = nowSeconds();
    let targetTime = this.endTime;
    if (this.beginTime && currentTime < this.beginTime) {
      targetTime = this.beginTime;
    }

    const initialTimeLeft = targetTime - currentTime;
    if (initialTimeLeft <= 0) {
      this._updateDisplay(0, this.endLabel);
      if (this.onFinish) this.onFinish();
      return;
    }

    this.timer = setInterval(() => {
      const now = nowSeconds();
      let nextTarget = this.endTime;
      let currentLabel = this.endLabel;
      if (this.beginTime && now < this.beginTime) {
        nextTarget = this.beginTime;
        currentLabel = this.beginLabel;
      }

      const timeLeft = nextTarget - now;
      if (timeLeft <= 0) {
        if (nextTarget === this.beginTime && this.endTime > now) {
          this._updateDisplay(this.endTime - now, this.endLabel);
          return;
        }

        this.stop();
        if (this.onFinish) this.onFinish();
        this._updateDisplay(0, this.endLabel);
        return;
      }

      this._updateDisplay(timeLeft, currentLabel);
    }, 1000);

    const initialLabel = this.beginTime && currentTime < this.beginTime ? this.beginLabel : this.endLabel;
    this._updateDisplay(initialTimeLeft, initialLabel);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * @param {number} timeLeft
   * @param {string} label
   */
  _updateDisplay(timeLeft, label) {
    if (!this.onUpdate) return;
    this.onUpdate(this._formatTime(timeLeft), label);
  }
}

class CountdownEasyElement extends HTMLElement {
  constructor() {
    super();
    this.countdown = null;
  }

  _getDefaultTemplate() {
    return `
            <div class="countdown-easy-inner-wrap">
                <div data-label class="c-label"></div>
                <div class="countdown-easy-inner">
                    <span class="c-item">
                        <b data-d></b>
                        <i>Days</i>
                    </span>
                    <em class="c-split"></em>
                    <span class="c-item">
                        <b data-h></b>
                        <i>Hrs</i>
                    </span>
                    <em class="c-split"></em>
                    <span class="c-item">
                        <b data-m></b>
                        <i>Mins</i>
                    </span>
                    <em class="c-split"></em>
                    <span class="c-item">
                        <b data-s></b>
                        <i>Secs</i>
                    </span>
                </div>
            </div>
        `;
  }

  connectedCallback() {
    const endTime = parseBeijingEndTime(this.getAttribute('end-time'));
    const beginTime = this.getAttribute('begin-time') ? parseBeijingEndTime(this.getAttribute('begin-time')) : undefined;
    const beginLabel = this.getAttribute('begin-label') || '';
    const endLabel = this.getAttribute('end-label') || '';
    const endStatus = this.getAttribute('end-status') || 'keep';

    if (!this.hasChildNodes() || this.children.length === 0) {
      this.innerHTML = this._getDefaultTemplate();
    }

    if (!endTime) return;

    this.countdown = new CountdownEasy({
      endTime,
      beginTime,
      beginLabel,
      endLabel,
      onUpdate: (time, label) => {
        const daysElement = this.querySelector('[data-d]');
        const hoursElement = this.querySelector('[data-h]');
        const minutesElement = this.querySelector('[data-m]');
        const secondsElement = this.querySelector('[data-s]');
        const labelElement = this.querySelector('[data-label]');

        if (daysElement) daysElement.innerText = time.days;
        if (hoursElement) hoursElement.innerText = time.hours;
        if (minutesElement) minutesElement.innerText = time.minutes;
        if (secondsElement) secondsElement.innerText = time.seconds;
        if (labelElement) labelElement.innerText = label;
      },
      onFinish: () => {
        if (endStatus === 'none') {
          this.style.display = 'none';
          return;
        }

        const display = this.querySelector('[data-display-panel]');
        const finishedMessage = this.querySelector('[data-finished-message]');
        if (display) display.style.display = 'none';
        if (finishedMessage) finishedMessage.style.display = 'block';
      },
    });

    this.countdown.start();
  }

  disconnectedCallback() {
    if (this.countdown) this.countdown.stop();
  }
}

if (!customElements.get('countdown-easy')) {
  customElements.define('countdown-easy', CountdownEasyElement);
}
